import { browser } from "$app/environment";

// ----------------------------------------------------------
// types, utilities
// ----------------------------------------------------------

export interface HotkeyId {
  hotkey: string;
  ctrlOrCmd?: boolean;
  shift?: boolean;
  alt?: boolean;
}

type HotkeyIdString =
  `${"ctrlOrCmd" | ""}-${"alt" | ""}-${"shift" | ""}-${string}`;

function hotkeyIdToUniqueString(id: HotkeyId): HotkeyIdString {
  return `${id.ctrlOrCmd ? "ctrlOrCmd" : ""}-${id.alt ? "alt" : ""}-${id.shift ? "shift" : ""}-${id.hotkey}`;
}

function hotkeyIdEquals(a: HotkeyId, b: HotkeyId): boolean {
  return hotkeyIdToUniqueString(a) === hotkeyIdToUniqueString(b);
}

export type HotkeyConfig = {
  key: HotkeyId;
  namespaceKey?: HotkeyId;
};

export type PauseConfig = {
  allowList?: HotkeyId[];
};

/** Keys that move the caret in a text field, so a matching hotkey would cancel the move. */
const CARET_KEYS = new Set([
  "ArrowUp",
  "ArrowDown",
  "ArrowLeft",
  "ArrowRight",
  "Home",
  "End",
  "PageUp",
  "PageDown",
]);

function matchesEvent(hotkeyId: HotkeyId, event: KeyboardEvent): boolean {
  const ctrlOrCmdPressed = isMac() ? event.metaKey : event.ctrlKey;

  if (
    isEventTargetATextInput(event.target) &&
    !hotkeyId.ctrlOrCmd &&
    (event.key.length === 1 || CARET_KEYS.has(event.key))
  ) {
    return false;
  }

  return (
    event.key === hotkeyId.hotkey &&
    ctrlOrCmdPressed === !!hotkeyId.ctrlOrCmd &&
    event.shiftKey === !!hotkeyId.shift &&
    event.altKey === !!hotkeyId.alt
  );
}

/**
 * Returns true if the user is on a Mac, or false if the user is on a Windows or Linux system.
 * @param ssrFallback - Used during SSR.
 * @returns True if the user is on a Mac, false otherwise.
 */
export function isMac(ssrFallback: boolean = true): boolean {
  if (typeof globalThis.window === "undefined") return ssrFallback;
  return globalThis.navigator.userAgent.toLowerCase().includes("mac");
}

/**
 * Creates a human-readable shortcut to represent this hotkey ID.
 *
 * e.g.: `{ cmdOrCtrl: true, hotKey: 'k' }` => "⌘K" on Mac, "Ctrl-K" elsewhere
 */
export function hotkeyToString(id: HotkeyId) {
  const mac = isMac();

  const modifiers: string[] = [];
  if (id.ctrlOrCmd) modifiers.push(mac ? "⌘" : "Ctrl");
  if (id.alt) modifiers.push(mac ? "⌥" : "Alt");
  if (id.shift) modifiers.push(mac ? "⇧" : "Shift");

  return [...modifiers, id.hotkey.toUpperCase()].join(mac ? "" : "-");
}

/**
 * Create a readable shortcut representing this hotkey config.
 *
 * e.g.:
 * ```
 * {
 *   namespaceKey: { cmdOrCtrl: true, hotKey: 'k' }
 *   key: { cmdOrCtrl: true, hotKey: 'J' },
 * }
 * ```
 * => "⌘K + ⌘J" on Mac, "Ctrl-K + Ctrl-J" elsewhere
 */
export function hotkeyConfigToString(config: HotkeyConfig) {
  if (config.namespaceKey)
    return `${hotkeyToString(config.namespaceKey)} + ${hotkeyToString(config.key)}`;

  return hotkeyToString(config.key);
}

function isModifierKey(key: string): boolean {
  return (
    key === "Control" ||
    key === "Shift" ||
    key === "Alt" ||
    key === "Meta" ||
    key === "AltGraph"
  );
}

function isEventTargetATextInput(target: EventTarget | null): boolean {
  if (!target || !(target instanceof Element)) return false;

  return (
    target instanceof HTMLInputElement ||
    target instanceof HTMLTextAreaElement ||
    target instanceof HTMLSelectElement ||
    // plaintext-only editors (the v2 search bar) are text fields too; missing
    // them lets plain-key hotkeys fire and swallow the typed character.
    ["true", "plaintext-only"].includes((target as HTMLElement).contentEditable)
  );
}

// ----------------------------------------------------------
// stateful behavior
// ----------------------------------------------------------

const SEQUENCE_TIMEOUT = 2000; // ms

const registeredKeys = new Map<
  symbol,
  { config: HotkeyConfig; callback: () => void }
>();

const registeredPausers = new Map<symbol, { config: PauseConfig }>();

let sequenceState:
  | { lastPressed?: undefined }
  | { lastPressed: number; lastKeyId: HotkeyId } = {};

if (browser) {
  window.addEventListener("keydown", onKeyDown);
}

function onKeyDown(event: KeyboardEvent) {
  // block if any pausers don't include our key in their allowlist
  if (
    registeredPausers.size > 0 &&
    [...registeredPausers.values()].some(
      ({ config }) =>
        config.allowList === undefined ||
        !config.allowList.some((hotkeyId) => matchesEvent(hotkeyId, event)),
    )
  )
    return;

  const isSequenceActive =
    sequenceState.lastPressed &&
    Date.now() < sequenceState.lastPressed + SEQUENCE_TIMEOUT;

  // old browsers are evil and don't like `Filter` on Map iterators
  const registeredEntries = [...registeredKeys.values()];

  // first check for second sequence entry
  if (isSequenceActive) {
    const matchingSequenceCompletionEntries = [
      ...registeredEntries.filter(
        ({ config }) =>
          config.namespaceKey &&
          hotkeyIdEquals(
            config.namespaceKey,
            (sequenceState as { lastPressed: number; lastKeyId: HotkeyId })
              .lastKeyId,
          ) &&
          matchesEvent(config.key, event),
      ),
    ];

    if (matchingSequenceCompletionEntries.length > 0) {
      event.preventDefault();
      matchingSequenceCompletionEntries.forEach(({ callback }) => callback());
      sequenceState = {};
      return;
    }
  }

  // then see if we have a direct keypress
  const matchingDirectEntries = [
    ...registeredEntries.filter(
      ({ config }) => !config.namespaceKey && matchesEvent(config.key, event),
    ),
  ];
  if (matchingDirectEntries.length > 0) {
    event.preventDefault();
    matchingDirectEntries.forEach(({ callback }) => callback());
    sequenceState = {};
    return;
  }

  // otherwise, if we're doing a sequence, start the state (automatically cleared after SEQUENCE_TIMEOUT ms)
  const matchingSequenceBeginEntries = [
    ...registeredEntries.filter(
      ({ config }) =>
        config.namespaceKey && matchesEvent(config.namespaceKey, event),
    ),
  ];
  if (matchingSequenceBeginEntries.length > 0) {
    event.preventDefault();
    sequenceState = {
      lastPressed: Date.now(),
      lastKeyId: matchingSequenceBeginEntries[0].config.namespaceKey!,
    };
    return;
  }

  // unrecognized, non-modifier keystroke breaks in-progress sequences.
  // e.g. pressing "g" then "x" should not let a later "h" complete "g h".
  if (isSequenceActive && !isModifierKey(event.key)) {
    sequenceState = {};
  }
}

// ----------------------------------------------------------
// main exports
// ----------------------------------------------------------

export const globalHotkey = {
  /**
   * Subscribe a new global hotkey callback.
   *
   * If multiple subscribers are active for the same key, they will all
   * be activated in sequence.
   *
   * @returns a cleanup method to unregister the callback.
   */
  addListener(config: HotkeyConfig, callback: () => void) {
    const registryKey = Symbol();
    registeredKeys.set(registryKey, { config, callback });

    return () => {
      registeredKeys.delete(registryKey);
    };
  },

  /**
   * Request a pause on global hotkeys, with an optional allowList.
   *
   * @returns a cleanup method to unregister the pause request.
   */
  addPauseRequest(config: PauseConfig) {
    const registryKey = Symbol();
    registeredPausers.set(registryKey, { config });

    return () => {
      registeredPausers.delete(registryKey);
    };
  },
};

// ----------------------------------------------------------
// svelte shorthand exports
// ----------------------------------------------------------

/**
 * Svelte utility for `globalHotkey.addListener`.
 *
 * Automatically cleans up listener on unmount.
 * Should be called unconditionally in a `script` tag.
 *
 * @param active - getter for whether the global hotkey
 *   should be enabled. Always active if not provided.
 */
export function useGlobalHotkey(
  config: HotkeyConfig,
  callback: () => void,
  active: () => boolean = () => true,
) {
  $effect(() => {
    if (active()) return globalHotkey.addListener(config, callback);
  });
}

/**
 * Svelte utility for `globalHotkey.addPauseRequest`.
 *
 * Automatically cleans up pause request on unmount.
 * Should be called unconditionally in a `script` tag.
 *
 * @param active - getter for whether the pause request
 *   should be enabled. Always active if not provided.
 */
export function useGlobalHotkeyPause(
  config: PauseConfig,
  active: () => boolean = () => true,
) {
  $effect(() => {
    if (active()) return globalHotkey.addPauseRequest(config);
  });
}
